/**
 * ChartBarInvestment.js
 *
 * Gráfico de barras empilhadas para Investment Management (DD Discounts)
 * Usa Chart.js para visualização de dados por período (Budget, Actual, Forecast)
 */

import React, { useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar } from "react-chartjs-2";
import AuthContext from "../../../../connections/auth/AuthProvider";

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
);

const useStyles = makeStyles(() => ({
  chartContainer: {
    position: "relative",
    height: "100%",
    width: "100%",
    minHeight: "200px",
  },
}));

/**
 * Função para gerar degradê de cores a partir de uma cor base
 */
const generateColorGradient = (baseColor, steps) => {
  // Converter hex para RGB
  const hex = baseColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const colors = [];
  for (let i = 0; i < steps; i++) {
    // Aumentar luminosidade progressivamente
    const factor = 1 + i * 0.25; // 0%, 25%, 50%, 75% mais claro
    const newR = Math.min(255, Math.floor(r + (255 - r) * (factor - 1)));
    const newG = Math.min(255, Math.floor(g + (255 - g) * (factor - 1)));
    const newB = Math.min(255, Math.floor(b + (255 - b) * (factor - 1)));
    colors.push(`rgb(${newR}, ${newG}, ${newB})`);
  }
  return colors;
};

/**
 * Formatar número para exibição
 */
const formatValue = (value) => {
  if (value === null || value === undefined) return "0";

  // Converter para milhões
  const millions = value / 1e6;

  if (Math.abs(millions) >= 1) {
    return millions.toFixed(2) + "M";
  }

  // Converter para milhares
  const thousands = value / 1e3;
  return thousands.toFixed(1) + "K";
};

export default function ChartBarInvestment({ data, currencyUnit }) {
  const classes = useStyles();
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails?.implementation_color || "#1976d2";

  // Gerar cores em degradê
  const colors = useMemo(
    () => generateColorGradient(clientColor, data.datasets.length),
    [clientColor, data.datasets.length],
  );

  // Configurar dados do gráfico com cores
  const chartData = useMemo(() => {
    return {
      labels: data.labels,
      datasets: data.datasets.map((dataset, index) => ({
        ...dataset,
        backgroundColor: colors[index],
        borderColor: colors[index],
        borderWidth: 0,
      })),
    };
  }, [data, colors]);

  // Opções do gráfico
  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "right",
          align: "start",
          labels: {
            boxWidth: 12,
            padding: 8,
            font: {
              size: 10,
            },
          },
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const label = context.dataset.label || "";
              const value = context.parsed.y || 0;
              return `${label}: ${currencyUnit} ${formatValue(value)}`;
            },
          },
        },
        datalabels: {
          color: "#fff",
          font: {
            size: 10,
            weight: "bold",
          },
          formatter: function (value) {
            // Só mostrar label se o valor for significativo (> 1% do total)
            if (value && Math.abs(value) > 1000) {
              return formatValue(value);
            }
            return "";
          },
          anchor: "center",
          align: "center",
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false,
          },
          ticks: {
            font: {
              size: 11,
            },
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          display: false, // Ocultar eixo Y
          grid: {
            display: false, // Remover grid
          },
        },
      },
    }),
    [currencyUnit],
  );

  return (
    <div className={classes.chartContainer}>
      <Bar data={chartData} options={options} />
    </div>
  );
}

ChartBarInvestment.propTypes = {
  data: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string).isRequired,
    datasets: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        data: PropTypes.arrayOf(PropTypes.number).isRequired,
      }),
    ).isRequired,
  }).isRequired,
  currencyUnit: PropTypes.string.isRequired,
};
